import React, { Component } from "react"
import { Button, Modal } from "react-bootstrap"
import DrugTestInfo from "./EventDetails/DrugTestInfo"
import TestSteps from "./EventDetails/TestSteps"
import OrderResultRecord from "../../api/OrderResults/OrderResultModel/OrderResultRecord"
import { Subscription } from "rxjs"
import { toast } from "react-toastify"
import { CurrentServiceDetails, UpdateServiceData } from "../../api/OrderResults/CurrentServiceDetails"
import ServiceDetails from "../../api/OrderResults/OrderResultModel/ServiceDetails"
import { ImplementationTypeGovt, StorageKey } from "../../config/Constants"
import TestEventUpdateData from "../../api/OrderResults/OrderResultModel/TestEventUpdateData"
import RandomPools from "../Randomization/RandomPools"
import SearchTestToMerge from "./SearchTestToMerge"
import PhoneType from "../../api/PhoneType"
import { APIError } from "../../api/ObservableFromFetch"

interface EventDetailsModalProps {
  details: OrderResultRecord
  onHide: (updated: boolean) => void
  onRefreshGrid?: () => void
  onMerge?: () => void
}

interface EventDetailsModalState {
  data?: ServiceDetails
  loading: boolean
  editMode: boolean
  testEventData?: TestEventUpdateData
  updated: boolean
}

export default class EventDetailsModal extends Component<EventDetailsModalProps, EventDetailsModalState> {
  subscription?: Subscription
  updateSubscription?: Subscription
  state: EventDetailsModalState = { data: undefined, loading: false, editMode: false, testEventData: undefined, updated: false }
  isOnTrack: boolean = sessionStorage.getItem(StorageKey.ImplementationType) === ImplementationTypeGovt

  componentDidMount() {
    this.setState({ loading: true })
    const details = this.props.details
    this.subscription = CurrentServiceDetails({ TestDetailClinicalID: details.TestDetailClinicalID, TestDetailID: details.TestDetailID }).subscribe(
      (data: ServiceDetails) => this.setState({ data: data, loading: false }),
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      },
      () => this.setState({ loading: false })
    )
  }

  handleAPIError = (error: Error | APIError) => {
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  handleEditModeChanged = (editMode: boolean) => {
    const { details: {HomePhone, WorkPhone, CellPhone} } = this.props
    const { CCFPersonnelID, CCFFirstName, CCFLastName } = this.state.data || {}

    this.setState({
      editMode: editMode,
      testEventData: {
        PersonnelID: CCFPersonnelID || "",
        FirstName: CCFFirstName || "",
        LastName: CCFLastName || "",
        Phones: {
          [PhoneType.Home]: { number: HomePhone, isRequired: false },
          [PhoneType.Work]: { number: WorkPhone, isRequired: true },
          [PhoneType.Cell]: { number: CellPhone, isRequired: false },
        }
      }
    })
  }

  handleUpdateFields = (testEventData: TestEventUpdateData) => {
    this.setState({ testEventData: testEventData })
  }

  handleSaveChanges = () => {
    this.updateSubscription && this.updateSubscription.unsubscribe()
    this.updateSubscription = UpdateServiceData(
      { ...this.state.testEventData as TestEventUpdateData,
        TestEventID: this.props.details.TestEventID,
        IDType: this.props.details.IDType,
        FormFoxTestAN: this.props.details.FormFoxTestAN
      }).subscribe(
      (data) => {
        toast.success(data)
        this.setState({updated: true}, () => this.props.onHide(this.state.updated))
      },
      (error) => {
        this.handleAPIError(error)
        this.props.onHide(this.state.updated)
      })
  }
  
  onHide = (collected?: boolean) => {
    if (collected) {
      this.props.onHide(this.state.updated)
      this.props.onRefreshGrid?.()
    }
  }

  isPhonesValid = () => {
    const { Phones } = this.state.testEventData || {}
    
    return (!Phones ||
      Object.values(Phones).every(phone =>
        !phone.isRequired ||
        phone.number.replace(/\D/g, "").length === 10)
      )
  }

  isValid = () => {
    return (
      this.state.testEventData && 
      this.state.testEventData.FirstName.length > 0 &&
      this.state.testEventData.LastName.length > 0 && 
      this.state.testEventData?.PersonnelID.length > 0 && 
      this.isPhonesValid()
    )
  }

  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
    this.updateSubscription && this.updateSubscription.unsubscribe()
  }

  render() {
    const currentRowInfo = this.props.details
    const currentServiceInfo = this.state.data
    return currentRowInfo && currentServiceInfo ? (
      <Modal show onHide={() => this.props.onHide(this.state.updated)} size="xl" dialogClassName="dark">
        <Modal.Header closeButton>
          <Modal.Title>VIEW/EDIT EVENT DETAILS</Modal.Title>
        </Modal.Header>
        <Modal.Body className="event-details">
          <DrugTestInfo
            currentRowInfo={currentRowInfo}
            updateFields={this.handleUpdateFields}
            currentServiceInfo={currentServiceInfo}
            isOnTrack={this.isOnTrack}
            editModeChanged={this.handleEditModeChanged}
            onMerge={this.props.onMerge}
            onHide={this.onHide} />
          <TestSteps currentRowInfo={currentRowInfo} currentServiceInfo={currentServiceInfo} isOnTrack={this.isOnTrack} />
        </Modal.Body>
        {this.state.editMode && <Modal.Footer>
          <Button variant="primary" disabled={!this.isValid()} onClick={this.handleSaveChanges} className="fixed-w5 ml-2">Save</Button>
        </Modal.Footer>}
      </Modal>
    ) : null
  }
}
